import React, { useEffect, useState } from 'react';
import { ActivityEntry, useKanbanStore } from '../../store/useKanbanStore';

interface ActivityLogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
}

const actionIcon = (action: string) => {
  if (action.includes('created')) return '📝';
  if (action.includes('updated') || action.includes('moved')) return '✏️';
  if (action.includes('deleted')) return '🗑️';
  if (action.includes('shared')) return '🔗';
  return '•';
};

const relativeTime = (dateValue: string) => {
  const timestamp = new Date(dateValue).getTime();
  if (Number.isNaN(timestamp)) return dateValue;
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const ActivityLog: React.FC<ActivityLogProps> = ({ isOpen, onClose, projectId }) => {
  const fetchActivity = useKanbanStore((state) => state.fetchActivity);
  const projects = useKanbanStore((state) => state.projects);
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedProjectId(projectId || '');
  }, [isOpen, projectId]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoading(true);
    void fetchActivity(selectedProjectId || undefined)
      .then((result) => { if (!cancelled) setEntries(result); })
      .catch(() => { if (!cancelled) setEntries([]); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, selectedProjectId, fetchActivity]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-0 sm:p-4 sm:backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Activity Log">
      <div className="flex h-full w-full sm:h-auto sm:max-h-[90vh] sm:w-auto sm:max-w-xl flex-col overflow-hidden rounded-none sm:rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-4 sm:px-6 dark:border-slate-800 dark:bg-slate-950"><div><h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Activity Log</h2><p className="text-xs text-slate-500 dark:text-slate-400">Recent board actions</p></div><button type="button" onClick={onClose} className="text-lg font-bold text-slate-400 hover:text-slate-700 dark:hover:text-slate-100" aria-label="Close activity log">✕</button></header>
        <div className="border-b border-slate-200 px-3 py-3 sm:px-6 dark:border-slate-800"><label htmlFor="activity-project-filter" className="mr-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Project</label><select id="activity-project-filter" value={selectedProjectId} onChange={(event) => setSelectedProjectId(event.target.value)} className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"><option value="">All boards</option>{projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></div>
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">{isLoading ? <p className="py-10 text-center text-sm text-slate-500">Loading activity…</p> : entries.length === 0 ? <p className="py-10 text-center text-sm text-slate-500">No activity to show.</p> : <ol className="space-y-1">{entries.map((entry) => <li key={entry.id} className="flex gap-3 rounded-lg px-2 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm dark:bg-slate-800" aria-hidden="true">{actionIcon(entry.action)}</span><div className="min-w-0 flex-1"><p className="text-sm text-slate-700 dark:text-slate-200"><span className="font-semibold">{entry.userName}</span>{' '}{entry.description.replace(`${entry.userName} `, '')}</p><time dateTime={entry.createdAt} className="mt-0.5 block text-xs text-slate-400">{relativeTime(entry.createdAt)}</time></div></li>)}</ol>}</div>
        <footer className="flex justify-end border-t border-slate-200 bg-slate-50 px-6 py-3 dark:border-slate-800 dark:bg-slate-950"><button type="button" onClick={onClose} className="rounded-md bg-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">Close</button></footer>
      </div>
    </div>
  );
};
